import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react'
import { AuthContextType, User, YogaSchool } from '../types'
import apiService from '../services/api'
import toast from 'react-hot-toast'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

// Global flag to track if logout event has been processed
// This prevents multiple instances/components from processing the same event
let globalLogoutProcessed = false

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [schools, setSchools] = useState<YogaSchool[] | null>(null)
  const [selectedSchool, setSelectedSchool] = useState<YogaSchool | null>(null)
  const [loading, setLoading] = useState(true)
  const isLoggingOutRef = useRef(false) // Ref to prevent multiple simultaneous logout calls
  const lastLogoutTimeRef = useRef(0) // Track last logout time for debouncing

  const logout = useCallback(() => {
    console.log('[LOGOUT-DEBUG] AuthContext logout() called', {
      isLoggingOutRef: isLoggingOutRef.current,
      globalLogoutProcessed,
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack?.split('\n').slice(1, 4).join('\n')
    })
    
    // Prevent multiple simultaneous logout calls
    if (isLoggingOutRef.current) {
      console.log('[LOGOUT-DEBUG] AuthContext logout() BLOCKED - already logging out')
      return
    }
    
    // Debounce: prevent logout if called within last 2 seconds
    const now = Date.now()
    const timeSinceLastLogout = now - lastLogoutTimeRef.current
    if (timeSinceLastLogout < 2000) {
      console.log('[LOGOUT-DEBUG] AuthContext logout() BLOCKED - debounce', {
        timeSinceLastLogout,
        lastLogoutTime: new Date(lastLogoutTimeRef.current).toISOString()
      })
      return
    }
    lastLogoutTimeRef.current = now
    
    isLoggingOutRef.current = true
    console.log('[LOGOUT-DEBUG] AuthContext logout() proceeding - flags set')
    
    try {
      // Don't wait for logout API - it's fire-and-forget
      console.log('[LOGOUT-DEBUG] Calling apiService.logout()')
      apiService.logout().catch((error) => {
        console.log('[LOGOUT-DEBUG] apiService.logout() error caught (ignored)', error)
      })
    } catch (error) {
      console.log('[LOGOUT-DEBUG] logout() try-catch error (ignored)', error)
    } finally {
      // Clear all auth data
      console.log('[LOGOUT-DEBUG] Clearing localStorage and state')
      localStorage.removeItem('authToken')
      localStorage.removeItem('user')
      localStorage.removeItem('schools')
      localStorage.removeItem('selectedSchool')
      setUser(null)
      setSchools(null)
      setSelectedSchool(null)
      toast.success('Logged out successfully')
      console.log('[LOGOUT-DEBUG] Logout complete - showing toast')
      
      // Reset flag after a delay
      setTimeout(() => {
        console.log('[LOGOUT-DEBUG] Resetting flags after timeout')
        isLoggingOutRef.current = false
        globalLogoutProcessed = false
      }, 3000)
    }
  }, [])

  // Listen for auth logout events from API interceptor
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    
    const handleAuthLogout = () => {
      console.log('[LOGOUT-DEBUG] Event listener received auth:logout event', {
        isLoggingOutRef: isLoggingOutRef.current,
        globalLogoutProcessed,
        timestamp: new Date().toISOString()
      })
      
      // Prevent multiple calls - check both local and global flags
      if (isLoggingOutRef.current || globalLogoutProcessed) {
        console.log('[LOGOUT-DEBUG] Event listener BLOCKED - already processing', {
          isLoggingOutRef: isLoggingOutRef.current,
          globalLogoutProcessed
        })
        return
      }
      
      // Debounce: clear any pending logout calls
      if (timeoutId) {
        console.log('[LOGOUT-DEBUG] Clearing previous timeout')
        clearTimeout(timeoutId)
      }
      
      // Set global flag immediately
      globalLogoutProcessed = true
      console.log('[LOGOUT-DEBUG] Setting globalLogoutProcessed = true, scheduling logout() in 100ms')
      
      // Debounce the actual logout call
      timeoutId = setTimeout(() => {
        console.log('[LOGOUT-DEBUG] Timeout fired - calling logout()')
        logout()
      }, 100)
    }

    window.addEventListener('auth:logout', handleAuthLogout)
    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout)
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [logout])

  useEffect(() => {
    let isMounted = true // Flag to prevent state updates if component unmounts

    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('authToken')
        const storedUser = localStorage.getItem('user')
        const storedSchools = localStorage.getItem('schools')
        const storedSelectedSchool = localStorage.getItem('selectedSchool')

        console.log('AuthContext init start', {
          hasToken: !!token,
          hasStoredUser: !!storedUser,
          hasStoredSchools: !!storedSchools,
          hasStoredSelectedSchool: !!storedSelectedSchool
        })

        if (token && storedUser) {
          if (isMounted) {
            setUser(JSON.parse(storedUser))
            if (storedSchools) {
              setSchools(JSON.parse(storedSchools))
            }
            if (storedSelectedSchool) {
              setSelectedSchool(JSON.parse(storedSelectedSchool))
            }
          }
          
          // Verify token and fetch fresh data
          try {
            const [userResponse, schoolsResponse] = await Promise.all([
              apiService.getUserProfile(),
              apiService.getSchoolProfile().catch(err => {
                // If schools endpoint fails
                console.warn('Failed to fetch schools:', {
                  message: err.message,
                  status: err.response?.status,
                  data: err.response?.data
                })

                // If it's a 404 (school not found), return empty schools array
                if (err.response?.status === 404) {
                  return { success: true, data: { schools: [] } }
                }

                // For other errors (500, auth issues, etc.), still return empty schools
                // but this might indicate a real problem
                return { success: true, data: { schools: [] } }
              })
            ])
            
            if (!isMounted) return // Don't update state if component unmounted
            
            if (userResponse.success) {
              const userData = userResponse.data
              
              console.log('AuthContext init: user data', {
                userId: userData?._id,
                role: userData?.role,
                schoolId: userData?.schoolId,
                schoolIds: (userData as any)?.schoolIds,
                school_id: (userData as any)?.school_id
              })

              // Validate user role - only YOGA_SCHOOL role can access admin dashboard
              if (userData.role !== 'YOGA_SCHOOL') {
                logout()
                toast.error('This account is not authorized to access the yoga school admin dashboard.')
                return
              }
              
              setUser(userData)
              localStorage.setItem('user', JSON.stringify(userData))
            }
            
            console.log('AuthContext init: schoolsResponse', {
              success: schoolsResponse.success,
              data: schoolsResponse.data,
              hasSchool: !!schoolsResponse.data?.school,
              hasData: !!schoolsResponse.data
            })

            if (schoolsResponse.success) {
              // V2 API returns single school object, not array
              const school = schoolsResponse.data?.school || schoolsResponse.data

              // Handle the case where we return { schools: [] } on error
              let schoolsArray: any[] = []
              if (school && !school.schools) {
                // Normal case: school object
                schoolsArray = [school]
              } else if (school?.schools) {
                // Error case: { schools: [] }
                schoolsArray = school.schools
              }

              console.log('AuthContext init: schools processed', {
                rawSchool: school,
                schoolsArray,
                schoolsArrayLength: schoolsArray.length,
                willSetSchools: schoolsArray.length > 0
              })

              if (isMounted) {
                setSchools(schoolsArray)
                localStorage.setItem('schools', JSON.stringify(schoolsArray))

                // Always refresh selectedSchool from latest API payload (avoid stale localStorage values)
                if (schoolsArray.length > 0) {
                  let nextSelectedSchool = schoolsArray[0]
                  if (storedSelectedSchool) {
                    try {
                      const stored = JSON.parse(storedSelectedSchool)
                      const matched = schoolsArray.find((s: any) => s?._id && stored?._id && s._id === stored._id)
                      if (matched) nextSelectedSchool = matched
                    } catch (parseError) {
                      console.warn('AuthContext: Failed to parse storedSelectedSchool, using first school', parseError)
                    }
                  }
                  setSelectedSchool(nextSelectedSchool)
                  localStorage.setItem('selectedSchool', JSON.stringify(nextSelectedSchool))
                } else {
                  // User doesn't have a school yet - they need to create one
                  console.warn('AuthContext: User does not have a school associated. They need to create one first.')
                  setSelectedSchool(null)
                  localStorage.removeItem('selectedSchool')
                }
              }
            } else {
              console.error('AuthContext: schoolsResponse was not successful:', schoolsResponse)
              if (isMounted) {
                setSchools([])
                setSelectedSchool(null)
              }
            }
          } catch (error: any) {
            // Only logout if it's an authentication error (401/403)
            if (error?.response?.status === 401 || error?.response?.status === 403) {
              if (isMounted) {
                logout()
              }
            } else {
              console.error('Error initializing auth:', error)
              // Keep user logged in but show error
            }
          }
        } else {
          // No token or user, set loading to false
          if (isMounted) {
            setLoading(false)
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        if (isMounted) {
          logout()
        }
      } finally {
        if (isMounted) {
          setLoading(false)
          console.log('AuthContext init: Initialization complete, loading set to false')
        }
      }
    }

    initializeAuth()

    // Cleanup function
    return () => {
      isMounted = false
    }
  }, []) // Empty dependency array - only run once on mount

  const login = async (email: string, password: string) => {
    try {
      setLoading(true)
      const response = await apiService.login(email, password)
      
      if (response.success) {
        const { user: userData, tokens } = response.data

        console.log('AuthContext login success', {
          userId: userData?._id,
          role: userData?.role,
          schoolId: userData?.schoolId,
          schoolIds: (userData as any)?.schoolIds,
          school_id: (userData as any)?.school_id,
          hasAccessToken: !!tokens?.accessToken
        })
        
        // Validate user role - only YOGA_SCHOOL role can login from admin dashboard
        if (userData.role !== 'YOGA_SCHOOL') {
          const error = new Error('This account is not authorized to access the yoga school admin dashboard. Please use the customer portal.')
          toast.error(error.message)
          throw error
        }
        
        // Store auth data
        localStorage.setItem('authToken', tokens.accessToken)
        if (tokens.refreshToken) {
          localStorage.setItem('refreshToken', tokens.refreshToken)
        }
        localStorage.setItem('user', JSON.stringify(userData))
        
        setUser(userData)
        
        // Fetch schools for UI purposes (school name, etc.) - but course creation can use user.schoolId directly
        try {
          const schoolsResponse = await apiService.getSchoolProfile().catch(err => {
            console.warn('Failed to fetch schools after login:', {
              message: err.message,
              status: err.response?.status,
              data: err.response?.data
            })

            // If it's a 404 (school not found), return empty schools array
            if (err.response?.status === 404) {
              return { success: true, data: { schools: [] } }
            }

            // For other errors, still return empty schools
            return { success: true, data: { schools: [] } }
          })

          console.log('AuthContext login: schoolsResponse', {
            success: schoolsResponse.success,
            data: schoolsResponse.data,
            hasSchool: !!schoolsResponse.data?.school,
            hasData: !!schoolsResponse.data
          })

          if (schoolsResponse.success) {
            // V2 API returns single school object, not array
            const school = schoolsResponse.data?.school || schoolsResponse.data

            // Handle the case where we return { schools: [] } on error
            let schoolsArray: any[] = []
            if (school && !school.schools) {
              // Normal case: school object
              schoolsArray = [school]
            } else if (school?.schools) {
              // Error case: { schools: [] }
              schoolsArray = school.schools
            }

            console.log('AuthContext login: schools processed', {
              rawSchool: school,
              schoolsArray,
              schoolsArrayLength: schoolsArray.length,
              willSetSchools: schoolsArray.length > 0
            })

            setSchools(schoolsArray)
            localStorage.setItem('schools', JSON.stringify(schoolsArray))

            // Auto-select the first school if available
            if (schoolsArray.length > 0) {
              console.log('AuthContext login: Auto-selecting first school:', schoolsArray[0]?._id)
              setSelectedSchool(schoolsArray[0])
              localStorage.setItem('selectedSchool', JSON.stringify(schoolsArray[0]))
            } else {
              console.warn('AuthContext login: User does not have a school associated.')
              setSelectedSchool(null)
              localStorage.removeItem('selectedSchool')
            }
          } else {
            console.error('AuthContext login: schoolsResponse was not successful:', schoolsResponse)
            setSchools([])
            setSelectedSchool(null)
            localStorage.setItem('schools', JSON.stringify([]))
            localStorage.removeItem('selectedSchool')
          }
        } catch (error) {
          console.error('Error fetching schools after login:', error)
          setSchools([])
          setSelectedSchool(null)
          localStorage.setItem('schools', JSON.stringify([]))
          localStorage.removeItem('selectedSchool')
        }
        
        toast.success('Login successful!')
        return userData
      } else {
        throw new Error(response.message || 'Login failed')
      }
    } catch (error: any) {
      const status = error?.response?.status
      const backendError =
        (typeof error?.response?.data?.error === 'object'
          ? error?.response?.data?.error?.message
          : error?.response?.data?.error) ||
        error?.response?.data?.message ||
        error?.message

      let message = backendError || 'Login failed'

      // Always normalize 401 login errors to user-friendly text unless it's email-verification related
      if (
        status === 401 &&
        !(typeof message === 'string' && message.toLowerCase().includes('verify your email'))
      ) {
        message = 'Invalid email or password. Please try again.'
      }

      // Portal mismatch is effectively an auth failure from user perspective
      if (
        status === 403 &&
        typeof message === 'string' &&
        message.toLowerCase().includes('insufficient permissions')
      ) {
        message = 'This account cannot access the yoga school dashboard.'
      }

      error.message = message
      // Don't show toast for "verify your email" - Login page handles it with a single combined message
      if (!message.toLowerCase().includes('verify your email')) {
        toast.error(message)
      }
      throw error
    } finally {
      setLoading(false)
    }
  }

  const updateProfile = async (data: Partial<User>) => {
    try {
      const response = await apiService.updateUserProfile(data)
      
      if (response.success) {
        const updatedUser = { ...user, ...response.data }
        setUser(updatedUser)
        localStorage.setItem('user', JSON.stringify(updatedUser))
        toast.success('Profile updated successfully!')
        return updatedUser
      } else {
        throw new Error(response.message || 'Profile update failed')
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Profile update failed'
      toast.error(message)
      throw error
    }
  }

  const selectSchool = (schoolId: string) => {
    if (schools) {
      const school = schools.find(s => s._id === schoolId)
      if (school) {
        setSelectedSchool(school)
        localStorage.setItem('selectedSchool', JSON.stringify(school))
        toast.success(`Switched to ${school.name}`)
      }
    }
  }

  const updateSchool = async (data: Partial<YogaSchool>) => {
    try {
      const response = await apiService.updateSchoolProfile(data)
      
      if (response.success) {
        let updatedSelectedSchool = null
        // Update the school in the schools array
        if (schools && selectedSchool) {
          const updatedSchools = schools.map(s => 
            s._id === selectedSchool._id ? { ...s, ...response.data } : s
          )
          setSchools(updatedSchools)
          localStorage.setItem('schools', JSON.stringify(updatedSchools))
          
          // Update selected school
          updatedSelectedSchool = { ...selectedSchool, ...response.data }
          setSelectedSchool(updatedSelectedSchool)
          localStorage.setItem('selectedSchool', JSON.stringify(updatedSelectedSchool))
        }
        toast.success('School profile updated successfully!')
        return updatedSelectedSchool
      } else {
        throw new Error(response.message || 'School update failed')
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'School update failed'
      toast.error(message)
      throw error
    }
  }

  const refreshSchools = async () => {
    try {
      console.log('refreshSchools: Starting to fetch school profile...')
      const schoolsResponse = await apiService.getSchoolProfile().catch(err => {
        console.error('refreshSchools: Failed to fetch schools:', {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data,
          url: err.config?.url
        })
        // If it's a 404 (school not found), return empty schools array
        if (err.response?.status === 404) {
          console.warn('refreshSchools: School not found (404)')
          return { success: false, data: null }
        }
        // For other errors, throw to be caught by outer catch
        throw err
      })

      console.log('refreshSchools: Response received:', {
        success: schoolsResponse.success,
        hasData: !!schoolsResponse.data,
        hasSchool: !!schoolsResponse.data?.school
      })

      if (schoolsResponse.success && schoolsResponse.data) {
        // V2 API returns single school object, not array
        const school = schoolsResponse.data?.school || schoolsResponse.data

        console.log('refreshSchools: School data:', {
          schoolId: school?._id,
          schoolName: school?.name,
          ownerId: school?.ownerId
        })

        // Handle the case where we return { schools: [] } on error
        let schoolsArray: any[] = []
        if (school && !school.schools && Object.keys(school).length > 0) {
          // Normal case: school object exists
          schoolsArray = [school]
          console.log('refreshSchools: Adding school to array:', school._id)
        } else if (school?.schools) {
          // Error case: { schools: [] }
          schoolsArray = school.schools
          console.log('refreshSchools: Using schools array from response:', schoolsArray.length)
        } else {
          console.warn('refreshSchools: No valid school found in response')
        }

        console.log('refreshSchools: Final schools array length:', schoolsArray.length)

        setSchools(schoolsArray)
        localStorage.setItem('schools', JSON.stringify(schoolsArray))

        // Auto-select the first school if available
        if (schoolsArray.length > 0) {
          console.log('refreshSchools: Setting selected school:', schoolsArray[0]._id)
          setSelectedSchool(schoolsArray[0])
          localStorage.setItem('selectedSchool', JSON.stringify(schoolsArray[0]))
        } else {
          console.warn('refreshSchools: No schools to select')
          setSelectedSchool(null)
          localStorage.removeItem('selectedSchool')
        }
      } else {
        console.error('refreshSchools: Response was not successful or had no data')
        setSchools([])
        setSelectedSchool(null)
        localStorage.setItem('schools', JSON.stringify([]))
        localStorage.removeItem('selectedSchool')
        throw new Error('Failed to fetch school profile')
      }
    } catch (error) {
      console.error('refreshSchools: Error refreshing schools:', error)
      setSchools([])
      setSelectedSchool(null)
      localStorage.setItem('schools', JSON.stringify([]))
      localStorage.removeItem('selectedSchool')
      throw error // Re-throw so caller knows it failed
    }
  }

  const value: AuthContextType = {
    user,
    schools,
    selectedSchool,
    login,
    logout,
    loading,
    updateProfile,
    selectSchool,
    updateSchool,
    refreshSchools,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
